import { makeAutoObservable } from "mobx";

import {
  ActionsForRole,
  type AvailableSections,
} from "@/api/admin/roleManagment";
import { restaurantApi } from "@/api/restaurant";
import type { AccessRoleType } from "@/api/restaurant/types";
import { PATHS } from "@/config/path";

import { RestaurantRole } from "./AuthStore";
import { rootStore } from "./RootStore";

type BaseRestaurant = {
  id: string;
  name: string;
  role: string | null;
  accessRole: AccessRoleType | null;
};

type WithImage = BaseRestaurant & {
  image?: string;
};

export type BrandRestaurant = WithImage;
export type BranchRestaurant = WithImage;
export type GroupRestaurant = BaseRestaurant;

export class RestaurantAccessStore {
  selectedGroup: GroupRestaurant | null = null;
  selectedBrand: BrandRestaurant | null = null;
  selectedBranch: BranchRestaurant | null = null;

  availableBrands: BrandRestaurant[] = [];

  brandBranchesMap = new Map<string, BranchRestaurant[]>();
  branchBrandMap = new Map<string, BrandRestaurant>();

  isBootstrapped = false;

  constructor() {
    makeAutoObservable(this);
  }

  initialize({
    brands,
    selectedGroup,
  }: {
    brands?: BrandRestaurant[];
    selectedGroup?: GroupRestaurant;
  }) {
    this.availableBrands = brands ?? [];
    this.selectedGroup = selectedGroup ?? null;

    if (this.availableBrands.length > 1) {
      this.selectedBrand = null;
      this.selectedBranch = null;
      return;
    }

    if (this.availableBrands.length === 1) {
      const onlyBrand = this.availableBrands[0];
      this.selectedBrand = onlyBrand;

      const branches = this.brandBranchesMap.get(onlyBrand.id) ?? [];

      if (branches.length > 1) {
        this.selectedBranch = null;
      } else {
        this.selectedBranch = branches[0] ?? null;
      }

      return;
    }

    this.selectedBrand = null;
    this.selectedBranch = null;
  }

  setSelectedBrand(brand: BrandRestaurant | null) {
    this.selectedBrand = brand;

    if (!brand) {
      this.selectedBranch = null;
      return;
    }

    const branches = this.brandBranchesMap.get(brand.id) ?? [];
    if (branches.length > 1) {
      this.selectedBranch = null;
    } else {
      this.selectedBranch = branches[0] ?? null;
    }
  }

  setSelectedBranch(branch: BranchRestaurant | null) {
    this.selectedBranch = branch;

    if (!branch) return;

    const brandOfBranch = this.branchBrandMap.get(branch.id);

    if (brandOfBranch) {
      this.selectedBrand = brandOfBranch;
    }
  }

  get availableBranches(): BranchRestaurant[] {
    if (this.selectedBrand) {
      return this.brandBranchesMap.get(this.selectedBrand.id) ?? [];
    }

    const allBranches: BranchRestaurant[] = [];
    this.brandBranchesMap.forEach(branches => {
      allBranches.push(...branches);
    });

    return allBranches;
  }

  get isBrandDisabled() {
    return (
      this.availableBrands.length <= 1 || rootStore.authStore.isBranchAccess
    );
  }

  get isBranchDisabled() {
    const branches = this.availableBranches;

    // "All brands" view — allow branch selection whenever any branches exist
    // (picking a branch also sets the corresponding brand).
    if (!this.selectedBrand) {
      return branches.length === 0;
    }

    return branches.length <= 1;
  }

  get hasNoBranchesForSelectedBrand(): boolean {
    if (!this.selectedBrand) return false;

    const branches = this.brandBranchesMap.get(this.selectedBrand.id);
    return !branches || branches.length === 0;
  }

  async bootstrapFromAssignments() {
    if (this.isBootstrapped) return;
    if (!rootStore.authStore.token) return;

    try {
      const assignment = await restaurantApi.getOptionsAssignments();
      if (!assignment) return;

      const groups: GroupRestaurant[] =
        assignment?.groups?.map(g => ({
          id: g.id,
          name: g.name,
          role: g.role,
          accessRole: g.accessRole,
        })) ?? [];

      const brands: BrandRestaurant[] =
        assignment?.groups?.flatMap(
          g =>
            g.brands?.map(b => ({
              id: b.id,
              name: b.name,
              role: b.role,
              accessRole: b.accessRole,
            })) ?? []
        ) ?? [];

      const brandBranchesMap = new Map<string, BranchRestaurant[]>();
      const branchBrandMap = new Map<string, BrandRestaurant>();

      assignment?.groups?.forEach(g => {
        g.brands?.forEach(b => {
          const brand: BrandRestaurant = {
            id: b.id,
            name: b.name,
            role: b.role,
            accessRole: b.accessRole,
          };

          const branches =
            b.branches?.map(br => {
              const branch = {
                id: br.id,
                name: br.name,
                role: br.role,
                accessRole: br.accessRole,
              };
              branchBrandMap.set(branch.id, brand);
              return branch;
            }) ?? [];

          brandBranchesMap.set(b.id, branches);
        });
      });

      this.brandBranchesMap = brandBranchesMap;
      this.branchBrandMap = branchBrandMap;

      rootStore.restaurantAccessStore.initialize({
        brands,
        selectedGroup: groups[0] ?? null,
      });

      this.isBootstrapped = true;
    } catch (error) {
      console.error("Bootstrap failed", error);
    }
  }

  get currentLevel(): RestaurantRole {
    const access = rootStore.authStore.currentRestaurantAccess;

    switch (access) {
      case RestaurantRole.GROUP:
        if (!this.selectedBrand) return RestaurantRole.GROUP;
        if (!this.selectedBranch) return RestaurantRole.BRAND;
        return RestaurantRole.BRANCH;

      case RestaurantRole.BRAND:
        if (!this.selectedBranch) return RestaurantRole.BRAND;
        return RestaurantRole.BRANCH;

      case RestaurantRole.BRANCH:
        return RestaurantRole.BRANCH;

      default:
        return RestaurantRole.GROUP;
    }
  }

  get currentEntityId(): string | null {
    switch (this.currentLevel) {
      case RestaurantRole.GROUP:
        return this.selectedGroup?.id ?? null;
      case RestaurantRole.BRAND:
        return this.selectedBrand?.id ?? null;
      case RestaurantRole.BRANCH:
        return this.selectedBranch?.id ?? null;
    }
  }

  get activeContext() {
    if (this.selectedBranch)
      return { level: RestaurantRole.BRANCH, id: this.selectedBranch.id };

    if (this.selectedBrand)
      return { level: RestaurantRole.BRAND, id: this.selectedBrand.id };

    return { level: RestaurantRole.GROUP, id: this.selectedGroup?.id };
  }

  private mergePermissions(
    permissionSets: Array<AccessRoleType["permissions"]>
  ): AccessRoleType["permissions"] {
    // "Maximum access" = if ANY entity has UPDATE, grant UPDATE (which implies READ).
    // Collect unique sections, and for each section take the highest action.
    const sectionMap = new Map<AvailableSections, Set<ActionsForRole>>();

    for (const permissions of permissionSets) {
      for (const p of permissions) {
        if (!sectionMap.has(p.section)) {
          sectionMap.set(p.section, new Set());
        }
        sectionMap.get(p.section)!.add(p.action);
      }
    }

    const merged: AccessRoleType["permissions"] = [];

    sectionMap.forEach((actions, section) => {
      // UPDATE is "higher" than READ — if any entity has UPDATE, use UPDATE
      if (actions.has(ActionsForRole.UPDATE)) {
        merged.push({ action: ActionsForRole.UPDATE, section });
      } else if (actions.has(ActionsForRole.READ)) {
        merged.push({ action: ActionsForRole.READ, section });
      }
    });

    return merged;
  }

  get currentPermissions() {
    const access = rootStore.authStore.currentRestaurantAccess;

    if (
      access === RestaurantRole.BRANCH &&
      !this.selectedBranch &&
      this.availableBranches.length > 1
    ) {
      return this.mergePermissions(
        this.availableBranches.map(b => b.accessRole?.permissions ?? [])
      );
    }

    if (this.selectedBranch?.accessRole?.permissions) {
      return this.selectedBranch.accessRole.permissions;
    }

    if (
      access === RestaurantRole.BRAND &&
      !this.selectedBrand &&
      this.availableBrands.length > 1
    ) {
      return this.mergePermissions(
        this.availableBrands.map(b => b.accessRole?.permissions ?? [])
      );
    }

    if (this.selectedBrand?.accessRole?.permissions) {
      return this.selectedBrand.accessRole.permissions;
    }

    if (this.selectedGroup?.accessRole?.permissions) {
      return this.selectedGroup.accessRole.permissions;
    }
    return [];
  }

  get canOpenLiveTransactions(): boolean {
    return this.currentLevel === RestaurantRole.BRANCH && !!this.selectedBranch;
  }

  getLiveTransactionsPathForCurrentSelection(): string {
    const { token } = rootStore.authStore;
    if (!token) return "";

    const id = this.selectedBranch?.id;
    return id ? PATHS.RESTAURANT.BRANCH.LIVE_TRANSACTIONS(id) : "#";
  }

  get isAllBranchesSelected(): boolean {
    return (
      this.currentLevel === RestaurantRole.BRANCH &&
      !this.selectedBranch &&
      this.availableBranches.length > 1
    );
  }

  get isAllBrandsSelected(): boolean {
    return (
      this.currentLevel === RestaurantRole.BRAND &&
      !this.selectedBrand &&
      this.availableBrands.length > 1
    );
  }

  get intercomCompany():
    | { company_id: string; name: string; type: RestaurantRole }
    | undefined {
    //  ALL BRANCHES
    if (this.isAllBranchesSelected && this.selectedBrand) {
      return {
        company_id: this.selectedBrand.id,
        name: this.selectedBrand.name,
        type: RestaurantRole.BRAND,
      };
    }

    // ALL BRANDS
    if (this.isAllBrandsSelected && this.selectedGroup) {
      return {
        company_id: this.selectedGroup.id,
        name: this.selectedGroup.name,
        type: RestaurantRole.GROUP,
      };
    }

    // BRANCH
    if (this.selectedBranch) {
      return {
        company_id: this.selectedBranch.id,
        name: this.selectedBranch.name,
        type: RestaurantRole.BRANCH,
      };
    }

    // BRAND
    if (this.selectedBrand) {
      return {
        company_id: this.selectedBrand.id,
        name: this.selectedBrand.name,
        type: RestaurantRole.BRAND,
      };
    }

    // GROUP
    if (this.selectedGroup) {
      return {
        company_id: this.selectedGroup.id,
        name: this.selectedGroup.name,
        type: RestaurantRole.GROUP,
      };
    }

    return undefined;
  }

  reset() {
    this.selectedGroup = null;
    this.selectedBrand = null;
    this.selectedBranch = null;
    this.availableBrands = [];
    this.brandBranchesMap.clear();
    this.branchBrandMap.clear();
    this.isBootstrapped = false;
  }
}
